import React, { useMemo, useState } from "react";

// Kanban view. Cards are grouped into vertical columns by the chosen status
// column's labels (plus an "Uncategorized" lane for items with no value).
// Drag-and-drop a card to change its status — the parent re-renders the
// table side via the same items state so both views stay in sync.
//
// Drag implementation is native HTML5 — no extra dep. Drag image is the
// card itself; the drop target is the column header strip + the card list
// area. We set `dataTransfer.setData("text/plain", itemId)` and read it
// back on drop.
export default function KanbanView({
  items,
  groupByColumn,
  board,
  readOnly,
  peopleIndex,
  onCardClick,
  onCellCommit,
  kindIndex,
}) {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverLane, setDragOverLane] = useState(null);

  const labels = groupByColumn?.settings?.labels || [];

  const lanes = useMemo(() => {
    const map = new Map();
    map.set("__uncategorized__", { id: null, label: "Uncategorized", color: "#dde2eb", items: [] });
    for (const l of labels) {
      map.set(l.id, { id: l.id, label: l.label, color: l.color, items: [] });
    }
    for (const it of items) {
      const v = it.column_values?.[groupByColumn._id]?.value;
      const lane = v && map.has(v) ? map.get(v) : map.get("__uncategorized__");
      lane.items.push(it);
    }
    return Array.from(map.values());
  }, [items, labels, groupByColumn]);

  const handleDragStart = (e, item) => {
    if (readOnly) return;
    setDraggingId(item._id);
    e.dataTransfer.setData("text/plain", item._id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, laneId) => {
    if (readOnly || !draggingId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverLane(laneId);
  };

  const handleDrop = (e, laneId) => {
    e.preventDefault();
    setDragOverLane(null);
    setDraggingId(null);
    if (readOnly) return;
    const itemId = e.dataTransfer.getData("text/plain");
    if (!itemId) return;
    const item = items.find((i) => i._id === itemId);
    if (!item) return;
    const currentVal = item.column_values?.[groupByColumn._id]?.value || null;
    const targetVal = laneId; // already null for uncategorized
    if (currentVal === targetVal) return;
    onCellCommit(item, groupByColumn._id, targetVal);
  };

  return (
    <div className="kanban-board">
      {lanes.map((lane) => {
        const isUncat = lane.id === null;
        const dragOver = dragOverLane === (lane.id ?? "__uncategorized__");
        return (
          <div
            key={lane.id || "__u__"}
            className={`kanban-lane ${dragOver ? "drag-over" : ""}`}
            onDragOver={(e) => handleDragOver(e, lane.id ?? "__uncategorized__")}
            onDragLeave={() => setDragOverLane(null)}
            onDrop={(e) => handleDrop(e, lane.id)}
          >
            <header
              className="kanban-lane-head"
              style={{ background: isUncat ? "#dde2eb" : lane.color, color: isUncat ? "#666" : "#fff" }}
            >
              <span className="lane-title">{lane.label}</span>
              <span className="lane-count">{lane.items.length}</span>
            </header>
            <div className="kanban-cards">
              {lane.items.length === 0 && <div className="kanban-empty">No items</div>}
              {lane.items.map((it) => (
                <KanbanCard
                  key={it._id}
                  item={it}
                  board={board}
                  peopleIndex={peopleIndex}
                  kindIndex={kindIndex}
                  draggable={!readOnly}
                  isDragging={draggingId === it._id}
                  onDragStart={(e) => handleDragStart(e, it)}
                  onDragEnd={() => { setDraggingId(null); setDragOverLane(null); }}
                  onClick={() => onCardClick(it)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({ item, board, peopleIndex, kindIndex, draggable, isDragging, onDragStart, onDragEnd, onClick }) {
  const kindDef = kindIndex[item.kind];
  const showBadge = kindDef && item.kind !== "task";

  // Show the 2-3 most informative columns on the card: the title column is
  // implicit (card heading), and we surface People and Date if present.
  const peopleCol = (board.columns || []).find((c) => c.type === "people");
  const dateCol = (board.columns || []).find((c) => c.type === "date");
  const priorityCol = (board.columns || []).find((c) => c.system_key === "priority");

  const peopleIds = item.column_values?.[peopleCol?._id]?.value;
  const peopleArr = Array.isArray(peopleIds) ? peopleIds : peopleIds ? [peopleIds] : [];
  const dateVal = item.column_values?.[dateCol?._id]?.value;
  const priorityVal = priorityCol ? item.column_values?.[priorityCol._id]?.value : null;
  const priorityLabel = priorityCol?.settings?.labels?.find((l) => l.id === priorityVal);

  return (
    <div
      className={`kanban-card ${isDragging ? "dragging" : ""}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {showBadge && (
        <span className="kanban-card-badge" style={{ background: kindDef.color }}>
          {kindDef.label}
        </span>
      )}
      <div className="kanban-card-title">{item.title}</div>

      <div className="kanban-card-meta">
        {priorityLabel && (
          <span
            className="kanban-card-priority"
            style={{ background: priorityLabel.color }}
          >
            {priorityLabel.label}
          </span>
        )}
        {dateVal && (
          <span className="kanban-card-date" title={dateVal}>
            {new Date(dateVal).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
        )}
        {peopleArr.length > 0 && (
          <div className="kanban-card-people">
            {peopleArr.slice(0, 3).map((uid) => {
              const p = peopleIndex[uid];
              const initials = p
                ? ((p.firstname?.[0] || "") + (p.lastname?.[0] || "")).toUpperCase() || "?"
                : "?";
              return (
                <span key={uid} className="avatar-chip" title={p ? `${p.firstname || ""} ${p.lastname || ""}` : uid}>
                  {initials}
                </span>
              );
            })}
            {peopleArr.length > 3 && <span className="people-more">+{peopleArr.length - 3}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
